/**
 * Unmounted top-level singletons — #44577.
 *
 * The pages RO-3-12 deferred (misc.tsx table) plus the workspace home that was
 * only mounted under `/projects/$projectSlug`. Each route here is a real body
 * (not `pending()`), with a route-discriminating `data-route` marker so the
 * SPA catch-all cannot greenwash an unported path.
 *
 *   /                         production verification shell (WP1b / #46598)
 *   /login                    passkey takeover (LoginClient)
 *   /help                     Help center landing
 *   /help/settings            static leaf ABOVE /help/$categorySlug (shadow fix)
 *   /dev                      developer preview hub
 *   /excellent/now            live agent "Now" view
 *   /notifications            notification center
 *   /integrations/connected   OAuth result takeover
 *   /onboarding/workspace     needs-workspace takeover
 *
 * Live Next pages under `excellent/app/**` are UNTOUCHED (D6).
 */

import * as React from "react";
import { z } from "zod";
import { createRoute } from "@tanstack/react-router";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  DashboardStatGrid,
  HelpArticleList,
  HelpCategoryGrid,
  HelpExternalLinksFooter,
  HelpExternalLinksEditor,
  HelpCategoryEditor,
  IntentBadge,
  ListCard,
  PageHeader,
  PageNav,
  SectionHeading,
  SettingsPageShell,
} from "@excellent/ui";
import type { DashboardStat } from "@excellent/core";
import {
  Activity as ActivityIcon,
  AlertTriangle,
  ArrowRight,
  Bell,
  Bot,
  CheckCircle2,
  Inbox,
} from "@excellent/ui/icons";
import { DarkRoom } from "@excellent/ui/blocks/play/dark-room";
import { apiCall, useApi, ApiError } from "@excellent/ui/api";
import { agentImprovementDashboard } from "@excellent/ui/api/contracts/agent-improvement-dashboard";
import { evidence } from "@excellent/ui/api/contracts/evidence";
import { universalVerification } from "@excellent/ui/api/contracts/universal-verification";
import { help } from "@excellent/ui/api/contracts/help";
import { agentRuns } from "@excellent/ui/api/contracts/agent-runs";
import { activity } from "@excellent/ui/api/contracts/activity";
import { otherActions } from "@excellent/ui/api/contracts/other-actions";
import {
  RouterLink as Link,
  useNavigate,
  useSearch,
} from "@excellent/ui/router";
import { LoginClient } from "../shared/login/login-client";
import { parseAuthPreviewState } from "../shared/login/preview-states";
import { HelpReseedButton } from "../shared/help/reseed-button";
import { Logo } from "../shared/chrome/logo";
import { rootRoute } from "./__root";
import { VerificationShell } from "../verification/shell";
import { AgentImprovementOverviewCard } from "../shared/agents/agent-improvement-overview-card";
import { VerifierHome } from "../shared/agents/verifier-home";
import { isVerificationRollbackEngaged } from "../verification/rollback-switch";
import { OldVerifierHomeBody } from "./classic";
import { routeSearchSchema } from "../shell/search";

// ── /  (production verification shell — WP11b / #46645, HG1) ────────────────
// Canonical launch route is `/`. WP1a chrome mounts here; page families are
// WP1c. WP11b cut `/` to the verification product. HG10 (#46594) deprecates
// `/work`, `/customers`, and `/classic` in this tree (redirect to `/`); the
// explorable archive is ~/Sites/excellent-classic.

/**
 * Production `/` (WP11b / #46645, HG1). Mounts the WP1a verification shell.
 * Page families are WP1c. HG10 deprecates `/work`, `/customers`, and
 * `/classic` here (redirect to `/`). Explore the old product in
 * ~/Sites/excellent-classic. The previous verifier console remains at
 * `/agents/verification` — it is not deleted.
 *
 * `/` is a bare-canvas path (`prototypes/bare-canvas.ts`) so the old product
 * chrome does not wrap this shell. `data-route="home"` keeps the #44577
 * singleton discriminator; `data-verification-shell="product"` is on the shell.
 */

/**
 * Ship-proof discriminator for THIS component — `pnpm check-home-surface-ships`.
 *
 * `data-route="home"` cannot serve as one. `excellent/client/verification/shell.tsx`
 * imports `HomePage` from `./home-page`, and that component stamps its own
 * `data-route="home"`; `companion-home.tsx` stamps a third. So the moment
 * `VerificationShell` is imported ANYWHERE the string is in the bundle, whether or
 * not `WorkspaceHome` renders anything at all — an assertion on it passes for the
 * wrong reason. Measured: 3 source emitters, 23 `data-route` hits in the artifact.
 *
 * These are module constants, not inline JSX text, because Prettier fills JSX at
 * printWidth 100 and would re-wrap a pinned phrase across a newline, breaking a
 * correct assertion. The gate re-derives uniqueness from source on every run, so
 * a second emitter appearing later fails the gate instead of silently weakening it.
 */
const HOME_SURFACE_MARK = "singletons-workspace-home-46598";

/** Set only when `/?actorId=` scopes the improvement read. See `WorkspaceHome`. */
const HOME_ACTOR_SCOPE_MARK = "singletons-home-actor-scope-46598";

function WorkspaceHome() {
  const navigate = useNavigate();
  const params = useSearch();
  // `/?actorId=<id>` scopes the agent-improvement read to one actor. The contract
  // has always carried the param (`agentImprovementQuery.actorId`, max 240); no
  // surviving client surface passed it, so it was unreachable from the UI.
  const actorId = params.get("actorId")?.trim().slice(0, 240) || undefined;
  const [soc2PreflightOpen, setSoc2PreflightOpen] = React.useState(false);
  const {
    data: listData,
    error: listError,
    isLoading: listLoading,
  } = useApi(evidence.list, { query: { status: "all", limit: 100 } });
  const { data: silent } = useApi(evidence.silentApprove, { query: undefined });
  const { data: readiness } = useApi(evidence.readiness, { query: undefined });
  const { data: universalOverview } = useApi(universalVerification.overview, { query: undefined });
  const { data: soc2Preflight } = useApi(
    evidence.soc2PreflightSample,
    soc2PreflightOpen ? { query: undefined } : null,
  );
  const {
    data: soc2RunsData,
    mutate: mutateSoc2Runs,
  } = useApi(evidence.soc2PreflightRuns, soc2PreflightOpen ? { query: {} } : null);
  const {
    data: soc2ImportOnboardingState,
    mutate: mutateSoc2ImportOnboardingState,
  } = useApi(
    evidence.soc2PreflightImportOnboardingState,
    soc2PreflightOpen ? { query: {} } : null,
  );
  const { data: soc2PolicyStudioState } = useApi(
    evidence.soc2PreflightPolicyStudioState,
    soc2PreflightOpen ? { query: {} } : null,
  );
  const {
    data: soc2BatchJobsData,
    mutate: mutateSoc2BatchJobs,
  } = useApi(evidence.soc2PreflightBatchJobs, soc2PreflightOpen ? { query: undefined } : null);
  const { data: templatesData } = useApi(evidence.templates, { query: undefined });
  const {
    data: workflowCandidatesData,
    mutate: mutateWorkflowCandidates,
  } = useApi(evidence.workflowCandidates, { query: undefined });
  const agentImprovement = useApi(agentImprovementDashboard.dashboard, {
    query: actorId ? { env: "real", actorId } : { env: "real" },
  });

  // WP11c / #46647 — HG9: restore the pre-cutover shell at `/` when the host
  // stamps data-verification-rollback=engaged. HG10 archive is the sibling folder.
  if (isVerificationRollbackEngaged()) {
    return (
      <div
        data-route="home"
        data-verification-rollback="engaged"
        data-classic-product="workspace-home"
        className="space-y-6 px-6 py-8"
      >
        <Alert data-verification-rollback-banner="true">
          <AlertTitle>Rolled back</AlertTitle>
          <AlertDescription>
            HG9 trigger fired: Hono GET / was not the verification IA, or GET
            /api/search was not JSON. The pre-cutover shell is restored here.
            The old product is archived at{" "}
            <code>~/Sites/excellent-classic</code> (HG10); `/classic` in this
            tree now redirects to `/`.
          </AlertDescription>
        </Alert>
        <OldVerifierHomeBody />
      </div>
    );
  }
  return (
    <div
      data-route="home"
      data-home-surface={HOME_SURFACE_MARK}
      data-home-actor-scope={actorId ? HOME_ACTOR_SCOPE_MARK : undefined}
    >
      <VerificationShell>
        <VerifierHome
          tasks={listData?.tasks ?? []}
          agentImprovementEvidence={
            <AgentImprovementOverviewCard
              data={agentImprovement.data}
              error={agentImprovement.error}
              fallbackActorId={actorId ?? "latest real agent"}
              fallbackEnvironment="real"
              isLoading={agentImprovement.isLoading}
            />
          }
          silent={silent}
          readiness={readiness}
          universalOverview={universalOverview}
          soc2Preflight={soc2Preflight}
          soc2ImportOnboardingState={soc2ImportOnboardingState}
          soc2PolicyStudioState={soc2PolicyStudioState}
          soc2BatchJobs={soc2BatchJobsData?.jobs ?? []}
          soc2Runs={soc2RunsData?.runs ?? []}
          soc2PreflightOpen={soc2PreflightOpen}
          templates={templatesData?.templates ?? []}
          workflowCandidates={workflowCandidatesData?.candidates ?? []}
          listError={listError}
          listLoading={listLoading && !listData}
          onOpen={(id) => navigate.push(`/runs/${id}`)}
          onOpenSoc2Preflight={() => setSoc2PreflightOpen(true)}
          onSoc2RunImported={() => {
            void mutateSoc2Runs();
            void mutateSoc2ImportOnboardingState();
            void mutateSoc2BatchJobs();
          }}
          onWorkflowCandidateSaved={() => {
            void mutateWorkflowCandidates();
          }}
        />
      </VerificationShell>
    </div>
  );
}

export const homeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: WorkspaceHome,
});

// ── /login ──────────────────────────────────────────────────────────────────

const loginSearchSchema = routeSearchSchema({
  preview: z.string().optional(),
  mode: z.string().optional(),
});

function LoginPage() {
  const params = useSearch();
  const previewing =
    params.get("preview") === "1" || params.get("preview") === "true";
  const preview = previewing
    ? parseAuthPreviewState(params.get("mode") ?? undefined)
    : undefined;
  return (
    <div data-route="login">
      <LoginClient preview={preview} />
    </div>
  );
}

export const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  validateSearch: loginSearchSchema,
  component: LoginPage,
});

// ── /help ───────────────────────────────────────────────────────────────────
// `?q=` is shell-wide (dock search) — read via the seam, do NOT re-declare.

function HelpLandingPage() {
  const sp = useSearch();
  const query = (sp.get("q") ?? "").trim();
  const { data: catData } = useApi(help.categories.list, { query: {} });
  const { data: artData } = useApi(help.articles.list, {
    query: { limit: 200 },
  });
  const { data: searchData } = useApi(
    help.articles.search,
    query.length > 0 ? { query: { q: query, limit: 25 } } : null,
  );

  const categories = catData?.categories ?? [];
  const articles = artData?.articles ?? [];
  const catById = new Map(categories.map((c) => [c.id, c]));
  const articleCounts = new Map<number, number>();
  for (const a of articles) {
    articleCounts.set(
      a.category_id,
      (articleCounts.get(a.category_id) ?? 0) + 1,
    );
  }
  const popular = [...articles]
    .sort((a, b) => (b.view_count ?? 0) - (a.view_count ?? 0))
    .slice(0, 5);
  const results = searchData?.hits ?? [];

  return (
    <main data-route="help" className="w-full space-y-6 px-6 py-8">
      <PageHeader
        title="Help center"
        description="Search the docs, browse by category, or reach the community."
      />

      {query ? (
        results.length > 0 ? (
          <HelpArticleList
            title={`Results for “${query}”`}
            items={results.map((r) => ({
              id: r.article_id,
              href: `/help/${r.category_slug}/${r.slug}`,
              title: r.title,
              subtitle: r.category_name,
            }))}
          />
        ) : (
          <section className="space-y-2">
            <SectionHeading title={`Results for “${query}”`} />
            <p className="type-body text-muted-foreground">
              No help articles match “{query}”. Try a different term, or browse
              by category below.
            </p>
            <HelpCategoryGrid
              categories={categories.map((c) => ({
                ...c,
                article_count: articleCounts.get(c.id) ?? 0,
              }))}
            />
          </section>
        )
      ) : (
        <>
          <HelpArticleList
            title="Popular articles"
            items={popular.map((p) => {
              const cat = catById.get(p.category_id);
              return {
                id: p.id,
                href: `/help/${cat?.slug ?? "general"}/${p.slug}`,
                title: p.title,
                subtitle: cat?.name,
                trailing: `${p.view_count} ${p.view_count === 1 ? "view" : "views"}`,
              };
            })}
          />
          <section className="space-y-2">
            <SectionHeading title="Browse by category" />
            <HelpCategoryGrid
              categories={categories.map((c) => ({
                ...c,
                article_count: articleCounts.get(c.id) ?? 0,
              }))}
            />
          </section>
        </>
      )}

      <HelpExternalLinksFooter />
    </main>
  );
}

export const helpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/help",
  component: HelpLandingPage,
});

// ── /help/settings  (STATIC — wins over /help/$categorySlug) ────────────────

function HelpSettingsPage() {
  return (
    <div data-route="help-settings">
      <SettingsPageShell
        title="Help center"
        description="External community links, categories, and re-seed."
      >
        <HelpExternalLinksEditor />
        <HelpCategoryEditor />
        <HelpReseedButton />
      </SettingsPageShell>
    </div>
  );
}

export const helpSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/help/settings",
  component: HelpSettingsPage,
});

// ── /dev ────────────────────────────────────────────────────────────────────

const TAKEOVERS: { href: string; label: string; note: string }[] = [
  { href: "/welcome?preview=1", label: "/welcome", note: "onboarding hub" },
  {
    href: "/onboarding/workspace",
    label: "/onboarding/workspace",
    note: "create-workspace takeover",
  },
  { href: "/login", label: "/login", note: "split sign-in takeover" },
  { href: "/join", label: "/join", note: "invite-accept takeover" },
  {
    href: "/transparency",
    label: "/transparency",
    note: "live telemetry disclosure",
  },
];

function DevPreviewHub() {
  // Dev-only surface. In production builds the SPA still mounts the route so
  // deep links resolve; the body is a short note rather than notFound (which
  // would need a server gate). Preview cookie toggling stays on the Next host.
  const isProd =
    typeof import.meta !== "undefined" &&
    // vitest/vite: import.meta.env.PROD
    Boolean((import.meta as { env?: { PROD?: boolean } }).env?.PROD);

  if (isProd) {
    return (
      <div data-route="dev" className="mx-auto max-w-3xl p-6">
        <PageHeader
          title="Developer preview"
          description="Not available in production builds."
        />
      </div>
    );
  }

  return (
    <div data-route="dev" className="mx-auto max-w-3xl space-y-6 p-6">
      <PageHeader
        title="Developer preview"
        description="View state-gated screens (onboarding, first-run takeovers) on this populated instance — no fresh DB, no data wipe. Read-only: preview relaxes the redirect gate so the screen renders; don't submit the forms against your real state."
      />
      <Card className="p-4">
        <h2 className="mb-1 type-body font-medium text-foreground">Takeovers</h2>
        <p className="mb-3 type-subtext text-muted-foreground">
          Screens that normally redirect away on a populated instance.
        </p>
        <ul className="space-y-2">
          {TAKEOVERS.map((t) => (
            <li key={t.href}>
              <Link
                href={t.href}
                className="type-body font-medium text-primary hover:underline"
              >
                {t.label}
              </Link>
              <span className="ml-2 type-subtext text-muted-foreground">
                {t.note}
              </span>
            </li>
          ))}
        </ul>
      </Card>
      <Card className="p-4">
        <h2 className="mb-1 type-body font-medium text-foreground">
          Auth states
        </h2>
        <p className="mb-3 type-subtext text-muted-foreground">
          Force each /login visual state with <code>?preview=1&amp;mode=…</code>
          .
        </p>
        <div className="flex flex-wrap gap-2">
          {[
            "welcome",
            "login",
            "register",
            "recover",
            "codes",
            "signed-in",
          ].map((mode) => (
            <Button key={mode} variant="outline" size="sm" asChild>
              <Link href={`/login?preview=1&mode=${mode}`}>{mode}</Link>
            </Button>
          ))}
        </div>
      </Card>
    </div>
  );
}

export const devRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dev",
  component: DevPreviewHub,
});

// ── /excellent/now ──────────────────────────────────────────────────────────

function NowPage() {
  const { data, isLoading } = useApi(agentRuns.feed, {
    query: { limit: 200 },
  });
  const rows = data?.rows ?? [];
  const active = rows.filter((r) => r.status === "running");

  return (
    <div data-route="excellent-now" className="flex flex-col">
      <div className="px-6 py-8">
        <PageHeader
          className="mb-5"
          title="Now"
          description="what every agent is doing right now"
        />
        <div className="space-y-8">
          <section>
            <SectionHeading
              className="mb-2"
              title={
                <span className="flex items-baseline gap-2">
                  <span>Active workers</span>
                  <span className="type-subtext text-muted-foreground">
                    {active.length}
                  </span>
                </span>
              }
            />
            {isLoading && !data ? (
              <p className="type-body text-muted-foreground">Loading…</p>
            ) : active.length === 0 ? (
              <p className="type-body text-muted-foreground">
                (no active workers)
              </p>
            ) : (
              <ul className="divide-y divide-border rounded-md border border-border">
                {active.map((r) => (
                  <li
                    key={r.id}
                    className="flex items-center justify-between gap-3 px-3 py-2 type-body"
                  >
                    <span className="font-medium text-foreground">
                      {r.role} · {r.agent_id}
                    </span>
                    <span className="type-subtext text-muted-foreground tabular-nums">
                      claimed {r.tasks_claimed} · shipped {r.tasks_shipped}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
          <section>
            <SectionHeading className="mb-2" title="Recent runs" />
            {rows.length === 0 && !isLoading ? (
              <p className="type-body text-muted-foreground">(no runs yet)</p>
            ) : (
              <ul className="divide-y divide-border rounded-md border border-border">
                {rows.slice(0, 20).map((r) => (
                  <li
                    key={r.id}
                    className="flex items-center justify-between gap-3 px-3 py-2 type-body"
                  >
                    <span className="text-foreground">
                      {r.role}{" "}
                      <span className="text-muted-foreground">#{r.id}</span>
                    </span>
                    <IntentBadge
                      intent={
                        r.status === "running"
                          ? "info"
                          : r.status === "completed"
                            ? "success"
                            : r.status === "failed" ||
                                r.status === "budget_exceeded"
                              ? "danger"
                              : "neutral"
                      }
                    >
                      {r.status}
                    </IntentBadge>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

export const excellentNowRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/excellent/now",
  component: NowPage,
});

// ── /notifications ──────────────────────────────────────────────────────────
// `?view=` is shell-wide — read via the seam.

function NotificationsPage() {
  const sp = useSearch();
  const rawView = sp.get("view");
  const view =
    rawView === "review" || rawView === "agents" || rawView === "activity"
      ? rawView
      : "all";

  const { data: runsData } = useApi(agentRuns.feed, { query: { limit: 50 } });
  const { data: actData } = useApi(activity.list, {});
  const runs = (runsData?.rows ?? []).filter((r) => r.status === "running");
  const activityEvents = (actData?.groups ?? []).reduce(
    (n, g) => n + (g.count ?? 0),
    0,
  );

  const stats: DashboardStat[] = [
    {
      title: "Needs you",
      value: "—",
      href: "/notifications?view=review",
      description: "Open the decision inbox for sign-offs",
    },
    {
      title: "Agents working",
      value: String(runs.length),
      href: "/notifications?view=agents",
      description: runs.length > 0 ? "Running right now" : "None running",
    },
    {
      title: "Recent activity",
      value: String(activityEvents),
      href: "/notifications?view=activity",
      description: "Events across the workspace",
    },
  ];

  const tabs = [
    {
      href: "/notifications",
      label: "All",
      icon: <Bell className="h-3.5 w-3.5" strokeWidth={1.75} />,
      activeHref: "/notifications",
    },
    {
      href: "/notifications?view=review",
      label: "Needs you",
      icon: <Inbox className="h-3.5 w-3.5" strokeWidth={1.75} />,
    },
    {
      href: "/notifications?view=agents",
      label: "Agents",
      icon: <Bot className="h-3.5 w-3.5" strokeWidth={1.75} />,
    },
    {
      href: "/notifications?view=activity",
      label: "Activity",
      icon: <ActivityIcon className="h-3.5 w-3.5" strokeWidth={1.75} />,
    },
  ];

  return (
    <div data-route="notifications">
      <PageNav
        section={{ label: "Notifications", href: "/notifications" }}
        title="Notifications"
        titleHref="/notifications"
        tabs={tabs}
      />
      <div className="@container/page flex flex-col gap-8 px-6 py-8">
        {view === "agents" ? (
          <ListCard
            title="Agents working"
            count={runs.length}
            empty="No agents running."
          >
            <ul className="divide-y divide-border">
              {runs.map((r) => (
                <li key={r.id} className="px-3 py-2 type-body">
                  {r.role} · {r.agent_id}
                </li>
              ))}
            </ul>
          </ListCard>
        ) : view === "activity" ? (
          <ListCard title="Recent activity" empty="No recent activity.">
            <p className="px-3 py-2 type-body text-muted-foreground">
              {activityEvents > 0
                ? `${activityEvents} event(s) in the activity rail.`
                : "Activity feed loads from the live activity contract."}
            </p>
          </ListCard>
        ) : view === "review" ? (
          <ListCard
            title="Needs your sign-off"
            empty="Nothing needs your sign-off — you're all caught up."
            action={
              <Link
                href="/queue"
                className="type-body text-primary hover:underline"
              >
                Full decision inbox
              </Link>
            }
          >
            <p className="px-3 py-2 type-body text-muted-foreground">
              Open the full decision inbox for promotion sign-offs and action
              approvals.
            </p>
          </ListCard>
        ) : (
          <>
            <DashboardStatGrid stats={stats} />
            <div className="grid grid-cols-1 gap-5 @xl/page:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
              <ListCard
                title="Needs your sign-off"
                viewAllHref="/notifications?view=review"
                empty="Nothing needs your sign-off — you're all caught up."
              >
                {null}
              </ListCard>
              <ListCard
                title="Agents"
                count={runs.length}
                viewAllHref="/notifications?view=agents"
                empty="No agents running."
              >
                <ul className="divide-y divide-border">
                  {runs.slice(0, 6).map((r) => (
                    <li key={r.id} className="px-3 py-2 type-body">
                      {r.role}
                    </li>
                  ))}
                </ul>
              </ListCard>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export const notificationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/notifications",
  component: NotificationsPage,
});

// ── /integrations/connected ─────────────────────────────────────────────────

const PROVIDER_LABEL: Record<string, string> = {
  xero: "Xero",
  quickbooks: "QuickBooks",
  hubspot: "HubSpot",
  github: "GitHub",
  gitlab: "GitLab",
  salesforce: "Salesforce",
  pipedrive: "Pipedrive",
  notion: "Notion",
  lever: "Lever",
  greenhouse: "Greenhouse",
  ashby: "Ashby",
  stripe: "Stripe",
  slack: "Slack",
};

function providerLabel(slug?: string): string {
  if (!slug) return "your account";
  return (
    PROVIDER_LABEL[slug] ??
    slug.replace(/[-_]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

const connectedSearchSchema = routeSearchSchema({
  status: z.string().optional(),
  provider: z.string().optional(),
  connection: z.string().optional(),
  reason: z.string().optional(),
});

function ConnectedPage() {
  // Seam useSearch so we don't close over the route const before it is bound.
  const params = useSearch();
  const statusRaw = params.get("status");
  const status =
    statusRaw === "error"
      ? "error"
      : statusRaw === "return"
        ? "return"
        : "success";
  const provider = params.get("provider") ?? undefined;
  const connection = params.get("connection") ?? undefined;
  const reason = params.get("reason") ?? undefined;
  const label = providerLabel(provider);
  const connectionHref = connection
    ? `/settings/integrations/connections/${connection}`
    : "/settings/integrations/connections";

  const view = {
    success: {
      glyph: <CheckCircle2 className="size-6 text-emerald-500" aria-hidden />,
      ring: "bg-emerald-500/10",
      title: `Connected to ${label}`,
      body: "Your account is linked. Excellent will start pulling your data into the schema in the background — no need to wait here.",
      actions: (
        <>
          <Button asChild className="w-full">
            <Link href={connectionHref}>
              Go to {label} <ArrowRight className="size-4" aria-hidden />
            </Link>
          </Button>
          <Button variant="outline" asChild className="w-full">
            <Link href="/settings/integrations/catalog">All integrations</Link>
          </Button>
        </>
      ),
    },
    error: {
      glyph: <AlertTriangle className="size-6 text-destructive" aria-hidden />,
      ring: "bg-destructive/10",
      title: provider
        ? `Couldn't connect ${label}`
        : "Couldn't finish connecting",
      body: "The authorization didn't complete. Nothing was saved to your workspace — you can safely try again.",
      actions: (
        <>
          <Button asChild className="w-full">
            <Link href="/settings/integrations/catalog">Try again</Link>
          </Button>
          <Button variant="outline" asChild className="w-full">
            <Link href="/settings/integrations/connections">
              Back to connections
            </Link>
          </Button>
        </>
      ),
    },
    return: {
      glyph: <CheckCircle2 className="size-6 text-foreground/70" aria-hidden />,
      ring: "bg-muted",
      title: "You're all set",
      body: "This connection finished in your browser. You can close this tab and return to Excellent.",
      actions: null as React.ReactNode,
    },
  }[status];

  return (
    <main
      data-route="integrations-connected"
      className="first-run grid min-h-screen place-items-center bg-background px-6 py-16"
    >
      <div className="w-full max-w-sm text-center">
        <Logo size={26} className="mx-auto mb-8 text-foreground/80" />
        <Card className="items-center px-8 py-10 text-center">
          <div
            className={`grid size-12 place-items-center rounded-full ${view.ring}`}
          >
            {view.glyph}
          </div>
          <h1 className="first-run-display mt-1 type-page-title text-foreground">
            {view.title}
          </h1>
          <p className="mt-3 max-w-xs type-body leading-body text-muted-foreground">
            {view.body}
          </p>
          {view.actions ? (
            <div className="mt-7 flex w-full flex-col gap-2">
              {view.actions}
            </div>
          ) : null}
          {status === "error" && reason ? (
            <p className="mt-6 max-w-xs break-words type-subtext text-muted-foreground/70">
              Details: {reason}
            </p>
          ) : null}
        </Card>
        <p className="mt-6 type-subtext text-muted-foreground/60">
          Excellent · Integrations
        </p>
      </div>
    </main>
  );
}

export const integrationsConnectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/integrations/connected",
  validateSearch: connectedSearchSchema,
  component: ConnectedPage,
});

// ── /onboarding/workspace ───────────────────────────────────────────────────

function CreateWorkspaceClient() {
  const navigate = useNavigate();
  const [name, setName] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [pending, setPending] = React.useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (pending || !name.trim()) return;
    setError(null);
    setPending(true);
    try {
      const res = await apiCall(otherActions.workspaceCreate, {
        body: { name: name.trim() },
      });
      if (res.ok) {
        navigate.push("/");
      } else if (res.reason === "unauthenticated") {
        setError("You need to sign in before creating a workspace.");
      } else {
        setError("Please enter a workspace name.");
      }
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.error
          : err instanceof Error
            ? err.message
            : "Could not create workspace.",
      );
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="w-full max-w-[26rem]">
      <p className="first-run-eyebrow text-muted-foreground">[ workspace ]</p>
      <h1 className="first-run-display mt-2 text-foreground">
        Name your workspace
      </h1>
      <p className="first-run-deck mt-2 text-muted-foreground">
        This is the team space you&apos;ll invite others into. You can rename it
        later.
      </p>
      <form onSubmit={onSubmit} className="mt-6 space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <input
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 type-body"
          placeholder="Acme Inc"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
        />
        <Button
          type="submit"
          variant="graphite"
          className="w-full gap-2"
          disabled={pending || !name.trim()}
        >
          {pending ? "Creating…" : "Create workspace"}
          <ArrowRight className="h-4 w-4" />
        </Button>
      </form>
    </div>
  );
}

function OnboardingWorkspacePage() {
  return (
    <div
      data-route="onboarding-workspace"
      className="first-run dark relative flex min-h-[calc(100vh-var(--desktop-titlebar-h,0px))] w-full flex-col overflow-hidden text-foreground"
    >
      <DarkRoom dawn vignette />
      <div className="relative z-[1] flex flex-1 items-center justify-center px-6 py-16">
        <CreateWorkspaceClient />
      </div>
    </div>
  );
}

export const onboardingWorkspaceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/onboarding/workspace",
  component: OnboardingWorkspacePage,
});
